import React from 'react';
import { 
  Trophy, Users, Target, Handshake, Shield, GraduationCap, 
  Building2, Globe, BarChart as ChartBar, Heart, Star, Coffee,
  Leaf, Recycle, AlertCircle, FileText, Download, Code, 
  LineChart, Search, RefreshCw, CheckCircle2, ArrowRight,
  Award, Eye, MessageSquare, Sparkles, Phone, Clock, 
  ShoppingBag, Calculator, Zap, TrendingUp
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import PageTransition from '../components/PageTransition';
import SEOHead from '../components/SEOHead';
import GradientHero from "../components/GradientHero";
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const About = () => {
  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "À propos de Neoqualis",
    "description": "Découvrez Neoqualis, ESN française spécialisée en Testing et Business Analysis",
    "mainEntity": {
      "@type": "Organization",
      "name": "Neoqualis",
      "foundingDate": "2022",
      "numberOfEmployees": "31",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Paris",
        "addressRegion": "Île-de-France",
        "addressCountry": "FR"
      }
    }
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://neoqualis.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Qui sommes-nous",
        "item": "https://neoqualis.com/about"
      }
    ]
  };

  const combinedJsonLd = [aboutJsonLd, breadcrumbJsonLd];

  const stats = [
    { 
      label: '~31 collaborateurs', 
      value: '31',
      description: 'salariés + freelances intégrés',
      color: 'text-primary'
    },
    { 
      label: '12 partenaires fidèles', 
      value: '12',
      description: 'Partenaires clients récurrents',
      color: 'text-secondary'
    },
    { 
      label: '+70% de croissance', 
      value: '70%',
      description: 'croissance moyenne',
      color: 'text-green-600'
    },
    { 
      label: 'Mixité H/F', 
      value: '50/50',
      description: '50% / 50% équilibrés',
      color: 'text-blue-600'
    }
  ];

  const values = [
    {
      title: 'Qualité',
      description: 'Exigence QA mesurée par des KPI, revues croisées, tests de performance quand c\'est utile, et intégration en CI/CD.',
      icon: Trophy,
      color: 'primary'
    },
    {
      title: 'Engagement',
      description: 'Tenir nos promesses : jalons et SLA clairs, reporting hebdo, et continuité assurée (backup consultant).',
      icon: Target,
      color: 'secondary'
    },
    {
      title: 'Transparence',
      description: 'Traçabilité exigences → tests, tableaux de bord partagés, décisions go/no-go objectivées.',
      icon: Eye,
      color: 'green-600'
    },
    {
      title: 'Éthique',
      description: 'RGPD by design, confidentialité, achats responsables (seconde main, reconditionné, recyclé pour l\'IT et le mobilier), filières réemploi/recyclage.',
      icon: Shield,
      color: 'blue-600'
    },
    {
      title: 'Humanisme',
      description: 'Proximité et respect : QVT, coaching de mission, intégration d\'alternants & stagiaires, nouveaux locaux accessibles PMR (accès, circulation, sanitaires).',
      icon: Heart,
      color: 'purple-600'
    }
  ];

  const services = [
    {
      title: 'Testing & QA',
      description: 'Stratégie, plans & cas, exécution, automatisation, mobile, performance.',
      icon: Shield,
      color: 'primary'
    },
    {
      title: 'Business Analysis / PO',
      description: 'Recueil & formalisation du besoin, user stories, critères d\'acceptation, priorisation Agile.',
      icon: Users,
      color: 'secondary'
    },
    {
      title: 'Automatisation & CI/CD',
      description: 'Cypress, Selenium, Robot Framework ; pipelines GitLab/Jenkins/Azure DevOps ; déflakiness & reporting.',
      icon: Code,
      color: 'green-600'
    },
    {
      title: 'Perf & Observabilité',
      description: 'JMeter, scénarios de charge, analyse des goulots, recommandations actionnables.',
      icon: ChartBar,
      color: 'blue-600'
    },
    {
      title: 'Formations (Qualis-Academy)',
      description: 'Testing, BA, Agilité — programmes opérationnels et coaching sur mission.',
      icon: GraduationCap,
      color: 'purple-600'
    }
  ];

  const methodology = [
    {
      title: 'Cadrage & risques',
      description: 'Périmètre, objectifs, scénarios critiques, AC.',
      icon: Search,
      step: 1
    },
    {
      title: 'Stratégie de test',
      description: 'Niveaux/types, données & environnements, outillage, intégration CI/CD.',
      icon: Target,
      step: 2
    },
    {
      title: 'Exécution & reporting',
      description: 'Couverture, défauts, vélocité ; tableaux de bord partagés.',
      icon: LineChart,
      step: 3
    },
    {
      title: 'Amélioration continue',
      description: 'Boucles courtes, déflakiness automation, capitalisation & standards.',
      icon: RefreshCw,
      step: 4
    }
  ];

  const sectors = [
    {
      title: 'Banque & Assurance',
      description: 'Paiements, KYC, sinistre, conformité.',
      icon: Building2,
      color: 'primary'
    },
    {
      title: 'Retraite & protection sociale',
      description: 'Calculs/référentiels, qualité de données.',
      icon: Clock,
      color: 'secondary'
    },
    {
      title: 'Télécoms & Industrie',
      description: 'Performance, selfcare, scalabilité.',
      icon: Phone,
      color: 'green-600'
    },
    {
      title: 'Hôtellerie/Services',
      description: 'Expérience omnicanale, réservation, fidélité.',
      icon: ShoppingBag,
      color: 'blue-600'
    }
  ];

  const rseHighlights = [
    {
      title: 'Parité & inclusion',
      description: 'Mixité 50/50, intégration d\'alternants & stagiaires.',
      icon: Users,
      color: 'primary'
    },
    {
      title: 'Accessibilité',
      description: 'Nouveaux bureaux accessibles PMR (accès, circulation, sanitaires).',
      icon: Heart,
      color: 'secondary'
    },
    {
      title: 'Numérique responsable',
      description: 'Éco-gestes, tri, réduction des impressions, pipelines optimisés.',
      icon: Leaf,
      color: 'green-600'
    },
    {
      title: 'Achats responsables',
      description: 'Priorité seconde main / reconditionné / recyclé (IT & mobilier), réemploi/fin de vie via filières adaptées.',
      icon: Recycle,
      color: 'blue-600'
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };

  return (
    <PageTransition>
      <SEOHead 
        title="Qui sommes-nous ? — Neoqualis"
        description="Neoqualis, ESN française spécialisée en Testing et Business Analysis."
        canonical="https://neoqualis.com/about"
        jsonLd={combinedJsonLd}
      />
      <div className="pt-16">
        {/* Hero */}
        <GradientHero
          title="Qui sommes-nous ?"
          subtitle="Neoqualis est une ESN française spécialisée en Testing (QA) et Business Analysis. Fondée en 2022 et basée en Île-de-France, nous sécurisons les parcours digitaux de nos clients grands comptes, du cadrage à la mise en production, avec une culture de proximité, de rigueur et de transparence."
          ctas={[
            { label: "Parler à un expert QA", to: "/contact", variant: "primary", iconRight: <ArrowRight /> },
            { label: "Découvrir nos expertises", to: "/expertise", variant: "secondary", iconRight: <ArrowRight /> }
          ]}
        />

        {/* Notre identité */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="max-w-4xl mx-auto text-center">
              <h2 className="heading mb-6">Notre identité</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Nous aidons les équipes produit et IT à réduire les risques, accélérer les cycles et améliorer 
                durablement la qualité. Notre modèle privilégie l'accompagnement opérationnel sur site ou en hybride, 
                des KPI partagés, et une traçabilité de bout en bout (exigences → tests → couverture → décision go/no-go).
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* Chiffres clés */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Chiffres clés</h2>
            </AnimatedSection>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mb-8">
              {stats.map(({ label, value, description, color }, i) => (
                <motion.div
                  key={label}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card text-center hover:scale-105 transition-transform">
                    <div className={`text-4xl font-bold ${color} mb-2`}>
                      {value}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1">{label}</div>
                    <div className="text-xs text-gray-600">{description}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center mb-8">
              <p className="text-gray-700 mb-4">
                <strong>Secteurs :</strong> Banque & Assurance, Retraite, Télécoms/Industrie, Hôtellerie/Services
              </p>
              {/* <Link 
                to="/blog"
                className="text-primary font-semibold inline-flex items-center hover:underline"
              >
                Voir nos références
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link> */}
            </div>
          </div>
        </section>

        {/* Nos valeurs */}
        <section id="values" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Nos valeurs</h2>
              <p className="subheading max-w-2xl mx-auto">
                Ces valeurs se traduisent en pratiques concrètes et mesurables sur chaque mission
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {values.map(({ title, description, icon: Icon, color }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                        <Icon className={`text-${color} w-8 h-8`} />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link 
                to="/csr"
                className="btn-primary inline-flex items-center"
              >
                Découvrir nos engagements RSE
                <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Ce que nous faisons */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Ce que nous faisons</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {services.map(({ title, description, icon: Icon, color }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                        <Icon className={`text-${color} w-8 h-8`} />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link 
                to="/expertise"
                className="btn-primary inline-flex items-center"
              >
                Nos expertises
                <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Méthode & gouvernance */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Méthode & gouvernance</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-8">
              {methodology.map(({ title, description, icon: Icon, step }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full text-center hover:scale-105 transition-transform">
                    <div className="flex items-center justify-center mb-4">
                      <div className="relative">
                        <div className="p-3 rounded-full bg-primary bg-opacity-10">
                          <Icon className="text-primary w-8 h-8" />
                        </div>
                        <div className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                          {step}
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-900">{title}</h3>
                    <p className="text-gray-700 text-sm">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Secteurs */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Secteurs d'activité</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-8">
              {sectors.map(({ title, description, icon: Icon, color }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                        <Icon className={`text-${color} w-8 h-8`} />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link 
                to="/expertise#domains"
                className="btn-primary inline-flex items-center"
              >
                Découvrir les domaines
                <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Innovation */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Innovation</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
              <motion.div
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full hover:scale-105 transition-transform">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-orange-100">
                      <Sparkles className="text-orange-600 w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold ml-4 text-gray-900">QualisAI</h3>
                  </div>
                  <p className="text-gray-700">
                    Analyse de spécifications (ambiguïtés, cohérence) et génération de scénarios Gherkin avec export 
                    Xray/Squash et passerelles vers Cypress/Selenium/JMeter. Résultat : meilleure couverture, cycles plus courts, moins de risques.
                  </p>
                </div>
              </motion.div>

              <motion.div
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full hover:scale-105 transition-transform flex flex-col justify-between">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-purple-100">
                        <GraduationCap className="text-purple-600 w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">Qualis-Academy</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Formations Testing, BA et Agilité pour ancrer les pratiques dans la durée.
                    </p>
                  </div>
                  <div className="mt-auto">
                    <a
                      href="https://qualis-academy.fr/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-800 hover:underline"
                    >
                      Visitez notre site
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="text-center">
              <Link 
                to="/contact?subject=QualisAI"
                className="btn-primary inline-flex items-center"
              >
                Demander une démo
                <Eye className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Engagement humain & RSE */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Engagement humain & RSE</h2>
              <p className="subheading max-w-2xl mx-auto">
                Aperçu de nos engagements concrets
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
              {rseHighlights.map(({ title, description, icon: Icon, color }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                        <Icon className={`text-${color} w-8 h-8`} />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-green-50 max-w-4xl mx-auto mb-8">
                <div className="flex items-center mb-4">
                  <Shield className="w-8 h-8 text-green-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">QVT & sécurité</h3>
                </div>
                <p className="text-gray-700">
                  Mutuelle, titres-resto, primes ; DUERP et diagnostic QVT.
                </p>
              </div>
            </AnimatedSection>

            <div className="text-center">
              <Link 
                to="/csr"
                className="btn-primary inline-flex items-center"
              >
                Voir la page RSE
                <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Notre équipe */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Notre équipe</h2>
              <p className="subheading max-w-3xl mx-auto">
                Une équipe à taille humaine : leads QA, BA/PO, ingénieurs automation/perf, coachs et managers de proximité. 
                Un modèle d'encadrement garantit la qualité des livrables et la continuité des missions (binômage, documentation, passation).
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* CTA final */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="heading mb-6">
                  Vous souhaitez en savoir plus ?
                </h2>
                <p className="subheading mb-8">
                  Lancer un audit QA (30 min) ou cadrer un projet
                </p>
                <div className="flex flex-col gap-4 justify-center max-w-md mx-auto">
                  <Link 
                    to="/contact"
                    className="btn-primary inline-flex items-center justify-center w-full"
                  >
                    Parler à un expert QA
                    <MessageSquare className="ml-2" />
                  </Link>
                  <Link 
                    to="/audit"
                    className="btn-secondary inline-flex items-center justify-center w-full"
                  >
                    Demander un audit QA (offert – 30 min)
                    <ArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default About;